import React from 'react'
import { PillTabs } from 'components/FbUI/PillTabs'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { FBPage } from 'components/FbUI/Page/FBPage'
import useDocumentTitle from 'components/useDocumentTitle'
import { ActivityTableV2 } from 'components/Tables/ActivityTableV2/ActivityTableV2'
import { useNavigate, useParams } from 'react-router-dom'
import { useNavbarStore } from '../../../components/Layout/navbar-store'
import {
  FBPageEyebrow,
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from '../../../components/FbUI/Page/styles'
import { WorkspaceOutlineIcon } from 'components/Layout/LeftNav/nav-icons'
import { PinToHomepageButton } from 'components/QuickAccess/PinToHomepageButton'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import { PAGE_KEYS } from 'services/homepage'

type ActivityTab = 'all' | 'my'

export function ActivityPageV2() {
  const navigate = useNavigate()
  const params = useParams()
  const selectedTab = (params?.type ?? 'all') as ActivityTab
  const store = useNavbarStore()

  const [totalActivity, setTotalActivity] = React.useState<number | null>(null)
  const [totalMyActivity, setTotalMyActivity] = React.useState<number | null>(
    null
  )

  useRecordVisit({ targetType: 'page', pageKey: PAGE_KEYS.activity })

  useDocumentTitle(
    `${selectedTab === 'my' ? 'My' : 'Team'} Activity - Activity - First Bite`
  )

  const formatTabTitle = (label: string, count: number | null) =>
    count == null ? (
      label
    ) : (
      <>
        {label}{' '}
        <span className="font-normal text-fg-secondary tabular-nums">
          ({count.toLocaleString()})
        </span>
      </>
    )

  const tabs = [
    {
      key: 'all' as const,
      title: formatTabTitle('Team activity', totalActivity),
    },
    {
      key: 'my' as const,
      title: formatTabTitle('My activity', totalMyActivity),
    },
  ]

  return (
    <FBPage
      innerPageClassName="!bg-surface-page !p-[8px]"
      containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
    >
      <FBPageHeader className="!mb-6">
        <FBPageTitleRow>
          <div className="flex flex-col items-start">
            <FBPageEyebrow>
              <WorkspaceOutlineIcon size={14} />
              Workspace
            </FBPageEyebrow>
            <FBPageTitle className="!text-fg">Activity</FBPageTitle>
          </div>
          <PinToHomepageButton
            target={{ targetType: 'page', pageKey: PAGE_KEYS.activity }}
          />
        </FBPageTitleRow>
      </FBPageHeader>

      <TableLinkStyleProvider variant="plain">
        <FBPageTableContainer
          className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
          style={{
            height: 'calc(var(--base-page-table-height) - 10px)',
          }}
        >
          <div className="shrink-0 border-b border-neutral-200 bg-white px-4 py-3">
            <PillTabs<ActivityTab>
              tabs={tabs}
              activeKey={selectedTab}
              onChange={(value) => {
                const url = `/activity/${value}/`
                navigate(url)
                store.setLastActivityVisited(url)
              }}
              idPrefix="activity"
              aria-label="Activity views"
              size="sm"
              className="w-fit"
            />
          </div>

          {/* sm toolbar density (32px search + Filter/Columns), matching the
              other table pages. */}
          <ToolbarDensityProvider value="sm">
            {selectedTab === 'all' && (
              <ActivityTableV2
                tableKey={'all-activity-table-v2'}
                setTotalRowsCount={setTotalActivity}
              />
            )}
            {selectedTab === 'my' && (
              <ActivityTableV2
                tableKey={'my-activity-table-v2'}
                setTotalRowsCount={setTotalMyActivity}
                myActivityOnly
              />
            )}
          </ToolbarDensityProvider>
        </FBPageTableContainer>
      </TableLinkStyleProvider>
    </FBPage>
  )
}
